import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AI_ASSISTANT_CONSENT_VERSION } from '@/constants/legalSuite';

interface AIConsentModalProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export function AIConsentModal({ open, onAccept, onDecline }: AIConsentModalProps) {
  const [checked, setChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!checked) return;
    setIsSubmitting(true);
    onAccept();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onDecline(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle className="text-lg">Uso de IA con tus datos financieros</DialogTitle>
          </div>
          <DialogDescription className="sr-only">Consentimiento para uso de inteligencia artificial</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-foreground leading-relaxed">
          <p>
            Para darte recomendaciones personalizadas, FOCO (Tu Dinero Tu Poder) puede usar un asesor con inteligencia artificial.
          </p>
          <p>
            Para ello, se utilizarán tus datos financieros (por ejemplo, FOCO Score, ingresos, gastos, deudas, metas y resúmenes mensuales) como contexto, junto con los mensajes que escribas en el chat.
          </p>
          <p>
            Estos datos se envían desde los servidores de FOCO (Supabase Edge Functions) a la{' '}
            <strong>OpenAI API</strong> para generar respuestas. El proveedor puede procesarlos fuera de tu
            país, según los términos vigentes de OpenAI. Si usas la <strong>importación desde PDF con IA</strong>, el
            archivo que subas también puede enviarse a OpenAI cuando inicies esa acción. Más detalle en nuestra{' '}
            <Link to="/privacy-policy" className="text-primary underline hover:text-primary/80" target="_blank">
              Política de privacidad
            </Link>{' '}
            y en la documentación interna del producto (<code className="text-xs">docs/security/openai-data-and-controls.md</code>).
          </p>
          <p className="text-muted-foreground italic">
            Las respuestas del asesor con IA son orientativas y de apoyo; no sustituyen asesoría legal, tributaria ni financiera profesional. Las decisiones finales sobre tu dinero son siempre tuyas.
          </p>
        </div>

        <div className="flex items-start gap-3 rounded-lg border p-3 bg-muted/30">
          <Checkbox
            id="ai-consent-check"
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            className="mt-0.5"
          />
          <label htmlFor="ai-consent-check" className="text-sm leading-relaxed cursor-pointer select-none">
            Acepto que FOCO use un asesor con inteligencia artificial y que mis mensajes de chat y un resumen de mis datos financieros (y, si uso la importación asistida, el PDF que suba) se envíen a OpenAI para generar recomendaciones o sugerencias de movimientos, según lo descrito en la Política de privacidad.
          </label>
        </div>

        <p className="text-xs text-muted-foreground">
          Consentimiento específico para IA — versión {AI_ASSISTANT_CONSENT_VERSION} (se guarda en tu cuenta al aceptar).
        </p>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onDecline} disabled={isSubmitting} className="w-full sm:w-auto">
            No usar IA ahora
          </Button>
          <Button onClick={handleAccept} disabled={!checked || isSubmitting} className="w-full sm:w-auto">
            {isSubmitting ? 'Procesando...' : 'Aceptar y usar IA'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
